/**
 * Account management API using Supabase Auth.
 * Handles email sign-up/in, Google/Apple OAuth, anonymous upgrade, and sign-out.
 */

import { getSupabase } from './supabaseClient.ts';

export interface AccountUser {
  id: string;
  email: string;
  isAnonymous: boolean;
  displayName: string;
}

export function extractDisplayName(user: { user_metadata?: Record<string, unknown>; email?: string | null }): string {
  const meta = user.user_metadata ?? {};
  if (typeof meta.full_name === 'string' && meta.full_name.trim()) return meta.full_name.trim();
  if (typeof meta.name === 'string' && meta.name.trim()) return meta.name.trim();
  if (user.email) return user.email.split('@')[0] || 'User';
  return 'User';
}

export async function signUp(email: string, password: string): Promise<AccountUser> {
  const sb = await getSupabase();
  if (!sb) throw new Error('Supabase not configured');
  const { data, error } = await sb.auth.signUp({ email, password });
  if (error) throw new Error(error.message);
  if (!data.user) throw new Error('Sign up failed');
  return {
    id: data.user.id,
    email: data.user.email ?? email,
    isAnonymous: data.user.is_anonymous ?? false,
    displayName: extractDisplayName(data.user),
  };
}

export async function signIn(email: string, password: string): Promise<AccountUser> {
  const sb = await getSupabase();
  if (!sb) throw new Error('Supabase not configured');
  const { data, error } = await sb.auth.signInWithPassword({ email, password });
  if (error) throw new Error(error.message);
  if (!data.user) throw new Error('Sign in failed');
  return {
    id: data.user.id,
    email: data.user.email ?? email,
    isAnonymous: data.user.is_anonymous ?? false,
    displayName: extractDisplayName(data.user),
  };
}

export async function signInWithGoogle(): Promise<void> {
  const sb = await getSupabase();
  if (!sb) throw new Error('Supabase not configured');
  const { error } = await sb.auth.signInWithOAuth({
    provider: 'google',
    options: { redirectTo: window.location.href },
  });
  if (error) throw new Error(error.message);
}

export async function signInWithApple(): Promise<void> {
  const sb = await getSupabase();
  if (!sb) throw new Error('Supabase not configured');
  const { error } = await sb.auth.signInWithOAuth({
    provider: 'apple',
    options: { redirectTo: window.location.href },
  });
  if (error) throw new Error(error.message);
}

export async function signOut(): Promise<void> {
  const sb = await getSupabase();
  if (!sb) return;
  await sb.auth.signOut();
}

export async function getCurrentUser(): Promise<AccountUser | null> {
  const sb = await getSupabase();
  if (!sb) return null;
  try {
    const { data } = await sb.auth.getUser();
    if (!data.user) return null;
    // Skip anonymous users — they aren't "signed in" from the account perspective
    // Check both native anonymous flag and Edge Function-created anonymous users
    if (data.user.is_anonymous) return null;
    if (data.user.user_metadata?.is_anonymous) return null;
    return {
      id: data.user.id,
      email: data.user.email ?? '',
      isAnonymous: false,
      displayName: extractDisplayName(data.user),
    };
  } catch {
    return null;
  }
}

/**
 * Upgrade an anonymous user to a real email account.
 * Preserves the user_id so all existing jobs, balance, and session_access rows stay linked.
 */
export async function upgradeAnonymousAccount(email: string, password: string): Promise<AccountUser> {
  const sb = await getSupabase();
  if (!sb) throw new Error('Supabase not configured');
  const { data, error } = await sb.auth.updateUser({ email, password });
  if (error) throw new Error(error.message);
  if (!data.user) throw new Error('Account upgrade failed');
  return {
    id: data.user.id,
    email: data.user.email ?? email,
    isAnonymous: false,
    displayName: extractDisplayName(data.user),
  };
}
